import { AlertTriangle, Eye, Info, Lock, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import type { UserListItem } from "@/modules/dashboard/users/types/users-api.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaCombobox } from "@/shared/components/forms/ColocaCombobox";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

import { ACCESS_SEARCH_MAX_LENGTH, ACCESS_SEARCH_MIN_LENGTH } from "../../constants/accessCatalog.constants";
import { useAccessCan } from "../../hooks/useAccessCan";
import { useRolesData, useUsersData } from "../../hooks/useAccessData";
import { ACCESS_ROLE_ALL, ACCESS_STATUS_ALL, type AccessStatusFilter } from "../../hooks/useAccessUsers";
import { useEffectiveSummaries } from "../../hooks/useEffectiveSummaries";
import { SettingsSection } from "../SettingsSection";
import { AccessHeaderStat } from "./AccessHeaderStat";
import { AccessUsersCards } from "./AccessUsersCards";
import type { AccessUsersListProps } from "./accessUsersList.types";
import { AccessUsersTable } from "./AccessUsersTable";
import { AssignRolesDrawer } from "./AssignRolesDrawer";
import { InviteUserDrawer } from "./InviteUserDrawer";

/**
 * "Users" section of Access & permissions. Lists team members with their assigned roles and the real
 * effective access (X/total) resolved deny-first against the backend roles catalog. The drawer
 * assigns/removes roles with auto-save. Gated by `users:read`; editing needs `users:update` +
 * `roles:update`.
 */
export function UsuariosSection() {
  const { t } = useTranslations("settings");
  const { user } = useAuth();
  const { canAll } = useAccessCan();

  // The catalog's own state matters: with `users:read` but not `roles:read` the catalog 403s, and resolving
  // summaries against an empty catalog would render every user as an authoritative-looking 0/N — on a
  // security screen that is indistinguishable from "this person really has no access". Degrade instead.
  const { roles, isLoading: catalogLoading, hasNoAccess: catalogUnavailable } = useRolesData();
  const {
    users,
    total,
    rolesByUser,
    rolesUnavailable,
    isLoading,
    hasNoAccess,
    assignRole,
    removeRole,
    page,
    pageSize,
    setPage,
    query,
    setSearch,
    isTermTooShort,
    status,
    setStatus,
    roleId,
    setRoleId,
    counts,
    loadFailed,
  } = useUsersData();

  // One aggregated warning per load when some users' roles couldn't be fetched, so an incomplete table is
  // never presented as authoritative truth. Keyed on the set instance — fires once per load, not per render.
  useEffect(() => {
    if (rolesUnavailable.size > 0) {
      colocaToast.warning(t("access_roles_partial_fail", { count: rolesUnavailable.size }));
    }
  }, [rolesUnavailable, t]);

  const [assignTarget, setAssignTarget] = useState<UserListItem | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  // The drawer edits ONE principal from the current page, so it must close when that page is replaced.
  //
  // Keyed on `users` rather than on the filter setters on purpose: a search change only reaches the wire
  // 350 ms later, and a drawer opened inside that window would survive the swap and end up editing a row
  // that is no longer rendered. This triggers exactly when the new page lands, so it also covers
  // pagination, the status/role filters, and the reload `RolesSection` fires from another section.
  //
  // Adjusted during render rather than in an effect — React's own pattern for resetting state when an
  // input changes, and the same one `AssignRolesDrawer` uses to re-seed its selection. It also closes the
  // drawer BEFORE the stale page is painted instead of one render later.
  const [renderedPage, setRenderedPage] = useState(users);
  if (renderedPage !== users) {
    setRenderedPage(users);
    setAssignTarget(null);
  }

  // Real edit permission. The "Ver como" strip below can only DOWNGRADE this for
  // a client-only preview — it never grants access the user does not have.
  // `:write`, not `:update` — `scopes[]` carries RBAC atoms, and the guard collapses `update` to `write`.
  const realCanManage = canAll("users:write", "roles:write");
  const [previewReadOnly, setPreviewReadOnly] = useState(false);
  const canManage = realCanManage && !previewReadOnly;

  // Inviting is org-scoped: the backend creates the invitation in the INVITER's organization and 403s a
  // caller without one, so the button is also hidden for org-less internal staff. `users:write` mirrors the
  // endpoint's permission — optimistic gating only; the backend 403 remains the authority (see useAccessCan).
  const canInvite =
    canAll("users:write") && !previewReadOnly && !!(user?.organization_id ?? user?.organization?.id);

  // Effective-access summaries memoized per principal (not recomputed on every keystroke). See Finding #5.
  const summaryById = useEffectiveSummaries(rolesByUser, roles);

  const roleOptions = useMemo(
    () => [
      { value: ACCESS_ROLE_ALL, label: t("access_filter_all_roles") },
      ...roles.map((role) => ({ value: role.id, label: role.name })),
    ],
    [roles, t],
  );

  // `satisfies` so a typo in a value is a build error rather than a 400 from `@IsEnum` at runtime.
  const statusOptions = useMemo(
    () =>
      [
        { value: ACCESS_STATUS_ALL, label: t("access_filter_all_statuses") },
        { value: "active", label: t("access_status_active") },
        { value: "inactive", label: t("access_status_inactive") },
        { value: "suspended", label: t("access_status_suspended") },
        { value: "pending_verification", label: t("access_status_pending_verification") },
      ] satisfies { value: AccessStatusFilter; label: string }[],
    [t],
  );

  const targetAssignedIds = useMemo(
    () => (assignTarget ? (rolesByUser[assignTarget.id] ?? []).map((r) => r.id) : []),
    [assignTarget, rolesByUser],
  );

  // One object for both faces of the list, so the table and the cards cannot be fed different data.
  const listProps: AccessUsersListProps = {
    users,
    rolesByUser,
    rolesUnavailable,
    summaryById,
    catalogUnavailable,
    canManage,
    currentUserId: user?.id,
    loadFailed,
    onOpenRoles: setAssignTarget,
  };

  return (
    <SettingsSection
      id="usuarios"
      eyebrow={t("access_eyebrow")}
      title={t("access_users_title")}
      sub={t("access_users_sub")}
      /* Stats come from `GET /internal/users/counts`, so they describe the whole filtered set rather than
         the page on screen. `counts === null` means the request FAILED and renders as "—": a zero here
         would read as "everyone has a role configured", the exact opposite of what a failure means. */
      headerRight={
        !hasNoAccess ? (
          <div className="flex items-center gap-3.5">
            <AccessHeaderStat label={t("access_stat_total")} value={counts ? counts.all : "—"} />
            <AccessHeaderStat label={t("access_stat_active")} value={counts ? counts.active : "—"} />
            {(counts === null || counts.no_roles > 0) && (
              <AccessHeaderStat
                label={t("access_stat_no_roles")}
                value={counts ? counts.no_roles : "—"}
                danger={counts !== null}
              />
            )}
          </div>
        ) : undefined
      }
    >
      {hasNoAccess ? (
        <ColocaEmpty
          surface="card"
          icon={Lock}
          title={t("access_no_access_title")}
          description={t("access_no_access_description")}
        />
      ) : (
        <>
          {/* Preview-as strip (client-only; can only downgrade to read-only) */}
          {realCanManage && (
            <div className="mb-3.5 flex flex-wrap items-center gap-2.5 rounded-md border border-dashed border-border bg-muted/40 px-2.5 py-2">
              <Eye size={13} className="text-muted-foreground" />
              <span className="text-[12px] text-muted-foreground">{t("access_view_as_label")}:</span>
              <div className="inline-flex rounded-md border border-border bg-card p-0.5">
                <button
                  type="button"
                  aria-pressed={!previewReadOnly}
                  onClick={() => setPreviewReadOnly(false)}
                  className={cn(
                    "h-6 rounded px-2.5 text-[11.5px]",
                    !previewReadOnly ? "bg-muted font-semibold text-foreground" : "text-muted-foreground",
                  )}
                >
                  {t("access_view_as_editor")}
                </button>
                <button
                  type="button"
                  aria-pressed={previewReadOnly}
                  onClick={() => setPreviewReadOnly(true)}
                  className={cn(
                    "h-6 rounded px-2.5 text-[11.5px]",
                    previewReadOnly ? "bg-muted font-semibold text-foreground" : "text-muted-foreground",
                  )}
                >
                  {t("access_view_as_viewer")}
                </button>
              </div>
              <span className="ml-auto font-mono text-[11px] text-muted-foreground/70">
                {t("access_view_as_demo")}
              </span>
            </div>
          )}

          {/* Two notices that can fire together, in ONE box.
              Their texts stay separate because the causes are separate — the viewer's own permission on one
              side, a catalog read that failed on the other — and merging the sentences would claim a single
              problem. What is merged is the chrome: two bordered boxes with their own padding and margin
              cost ~45px of a phone screen before anything actionable, for no added meaning.

              The roles catalog 403'ing (`roles:read` missing) is why effective access renders as unknown
              ("?") rather than an authoritative 0/N, and why role management is disabled. */}
          {(!canManage || catalogUnavailable) && (
            <div className="mb-3.5 divide-y divide-dashed divide-warning/30 rounded-md border border-warning/40 bg-warning/10 text-[12px] text-foreground">
              {!canManage && (
                <div className="flex items-start gap-2.5 px-3 py-2.5">
                  <Lock size={12} className="mt-0.5 shrink-0 text-warning" />
                  {t("access_readonly_note")}
                </div>
              )}
              {catalogUnavailable && (
                <div className="flex items-start gap-2.5 px-3 py-2.5 leading-relaxed">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0 text-warning" />
                  {t("access_catalog_unavailable")}
                </div>
              )}
            </div>
          )}

          {/* Toolbar — every filter is resolved by the database, so it reaches every user, not just the
              page on screen. Changing any of them also closes the drawer: it edits one row of the page
              that is about to be replaced. */}
          {/* The widths are fixed from `sm` up, which is the layout this toolbar was designed for. Below
              that they would wrap into four ragged lines with dead space on the right and the primary
              action landing alone — so each control takes the full width instead. Every mobile class has
              an `sm:` counterpart, so from 640px nothing changes. */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="w-full sm:min-w-[220px] sm:max-w-[300px] sm:flex-1">
              <ColocaSearchInput
                value={query}
                onValueChange={setSearch}
                placeholder={t("access_search_person_placeholder")}
                maxLength={ACCESS_SEARCH_MAX_LENGTH}
              />
            </div>
            <div className="w-full sm:w-[170px]">
              <ColocaSelect
                value={status}
                onValueChange={(next) => setStatus(next as AccessStatusFilter)}
                options={statusOptions}
              />
            </div>
            {!catalogUnavailable && (
              <div className="w-full sm:w-[210px]">
                <ColocaCombobox
                  value={roleId}
                  onValueChange={(next) => setRoleId(next ?? ACCESS_ROLE_ALL)}
                  options={roleOptions}
                  placeholder={t("access_role_filter_placeholder")}
                  aria-label={t("access_role_filter_placeholder")}
                />
              </div>
            )}
            {canInvite && (
              <div className="w-full sm:ml-auto sm:w-auto">
                <ColocaButton
                  variant="primary"
                  size="sm"
                  icon={Plus}
                  onClick={() => setInviteOpen(true)}
                  className="w-full sm:w-auto"
                >
                  {t("access_invite_action")}
                </ColocaButton>
              </div>
            )}
          </div>

          {/* Typed something too short to search. Say so instead of firing a request the API would 400 —
              the other filters keep applying meanwhile. */}
          {isTermTooShort && (
            <div className="mb-3 flex items-center gap-2 text-[12px] text-muted-foreground">
              <Info size={13} className="shrink-0" />
              {t("access_search_min_hint", { min: ACCESS_SEARCH_MIN_LENGTH })}
            </div>
          )}

          {/* List (the catalog load gates it too — summaries resolved against a half-loaded catalog would
              flash misleading 0/N counts) */}
          {(isLoading && users.length === 0) || catalogLoading ? (
            <div className="space-y-2 rounded-md border border-border p-3">
              <ColocaSkeleton className="h-12 w-full" />
              <ColocaSkeleton className="h-12 w-full" />
              <ColocaSkeleton className="h-12 w-full" />
            </div>
          ) : (
            /* Skeleton only on the FIRST load. Refetching a page keeps the rows and dims them: swapping the
               list for a skeleton on every keystroke destroys the context the user is reading and makes the
               section jump. */
            <div
              className={cn("transition-opacity", isLoading && "pointer-events-none opacity-60")}
              aria-busy={isLoading}
            >
              {/* Two faces of the same page, picked by CSS rather than by `useIsMobile()`. That is what the
                  four sibling access surfaces already do (/app/users and the three /app/access/*), and it
                  sidesteps the hook's first render, where `isMobile` is still `undefined` and resolves to
                  desktop — a flash of the clipped grid on every phone. Both faces take the same props. */}
              <div className="hidden xl:block">
                <AccessUsersTable {...listProps} />
              </div>
              <div className="xl:hidden">
                <AccessUsersCards {...listProps} />
              </div>
            </div>
          )}

          {/* `total` is the backend's count for the CURRENT filters, so the summary describes the filtered
              set rather than the table. Rendered only when there is more than one page. */}
          {total > pageSize && (
            <div className="mt-3">
              <ColocaPagination page={page} perPage={pageSize} total={total} onPageChange={setPage} />
            </div>
          )}

          <div className="mt-2.5 flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <Info size={12} />
            {t("access_users_footnote")}
          </div>
        </>
      )}

      <AssignRolesDrawer
        principal={
          assignTarget
            ? {
                id: assignTarget.id,
                name: assignTarget.full_name,
                kind: "person",
                subtitle: assignTarget.email,
              }
            : null
        }
        open={!!assignTarget}
        onOpenChange={(open) => {
          if (!open) setAssignTarget(null);
        }}
        roles={roles}
        assignedRoleIds={targetAssignedIds}
        canManage={canManage}
        onAssign={(roleId) => assignRole(assignTarget!.id, roleId)}
        onRemove={(roleId) => removeRole(assignTarget!.id, roleId)}
      />

      <InviteUserDrawer
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={() => setInviteOpen(false)}
      />
    </SettingsSection>
  );
}
